import {
  ArrowsClockwiseIcon,
  BarcodeIcon,
  ReceiptIcon,
} from '@phosphor-icons/react'

import '../landing/landing.css'

import { useAuth } from '../../auth/AuthProvider.tsx'
import { BarcodeStamp } from '../landing/Barcode.tsx'
import { HeroGlow } from '../landing/HeroGlow.tsx'
import { navigateOnPlainClick } from '../../shell/links.ts'

// The signed-out state for /dashboard/bookings — the pitch for the dashboard
// lives where the dashboard would be, same reasoning as the webhooks screen's
// SignedOut. Leaner than that one on purpose: this page's sample content is
// the table itself, which is not worth fabricating; the copy carries it.

const DOCS_PATH = '/docs/api'

const PERKS = [
  {
    icon: ReceiptIcon,
    title: 'Every API order in one list',
    body: 'Status, freight, price, and the identifiers you need to reconcile it on your side.',
  },
  {
    icon: BarcodeIcon,
    title: 'The request behind each row',
    body: 'Open a booking for a copyable reconstruction of the request that produced it.',
  },
  {
    icon: ArrowsClockwiseIcon,
    title: 'Webhook history per booking',
    body: 'What Curri sent about this order, what your endpoint answered, and a resend for failures.',
  },
] as const

const SignInCta = () => {
  const { state, signIn } = useAuth()
  const signingIn = state.status === 'authenticating'

  return (
    <button
      className="landing-btn landing-btn-brand"
      onClick={signingIn ? undefined : signIn}
      style={{ fontFamily: 'inherit', cursor: signingIn ? 'default' : 'pointer' }}
      type="button"
    >
      {signingIn ? 'Signing in…' : 'Sign in'}
    </button>
  )
}

export const SignedOut = ({
  onNavigate,
}: {
  onNavigate: (path: string) => void
}) => (
  <div style={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto' }}>
    <HeroGlow blobCount={2} height={640} />
    <div
      style={{
        position: 'relative',
        maxWidth: 720,
        margin: '0 auto',
        padding: '96px 56px 40px',
      }}
    >
      <div
        className="landing-up"
        style={{ display: 'flex', alignItems: 'center', gap: 14 }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.08em',
            color: 'var(--curri-text-secondary)',
          }}
        >
          BUILD · BOOKINGS
        </span>
        <BarcodeStamp tone="strong" />
      </div>

      <h1
        className="landing-up"
        style={{
          margin: '14px 0 0',
          fontSize: 'clamp(40px, 4.5vw, 54px)',
          lineHeight: 1.1,
          animationDelay: '0.03s',
        }}
      >
        Bookings
      </h1>
      <p
        className="landing-up"
        style={{
          margin: '16px 0 0',
          fontSize: 17,
          lineHeight: 1.6,
          color: 'var(--curri-text-secondary)',
          maxWidth: '46ch',
          textWrap: 'pretty',
          animationDelay: '0.06s',
        }}
      >
        Sign in to see every order your integration created through the Curri
        API — with the request that produced it, the webhooks we sent about it,
        and the identifiers to reconcile it on your side.
      </p>

      <div
        className="landing-up"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 26,
          animationDelay: '0.1s',
        }}
      >
        <SignInCta />
        <a
          className="landing-btn landing-btn-outline"
          href={DOCS_PATH}
          onClick={navigateOnPlainClick(onNavigate, DOCS_PATH)}
        >
          Read the API docs
        </a>
      </div>

      <div
        className="landing-up"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginTop: 32,
          animationDelay: '0.16s',
        }}
      >
        {PERKS.map(perk => (
          <div
            key={perk.title}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}
          >
            <perk.icon
              color="var(--curri-accent)"
              size={17}
              style={{ flex: 'none', marginTop: 2 }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{perk.title}</div>
              <div
                style={{
                  marginTop: 2,
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: 'var(--curri-text-tertiary)',
                  maxWidth: '56ch',
                }}
              >
                {perk.body}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)
